import React, { useRef, useCallback } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { AtlasV2TrafficTopSectionV2 } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficTopSectionV2';
import AtlasV2TotalTrafficV2TotalTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficV2TotalTrafficSplineChart';
import AtlasV2TotalTrafficKeywordCountWithTotalTraffic from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficKeywordCountWithTotalTraffic';
import AtlasV2TotalTrafficV2ShareOfTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficV2ShareOfTrafficSplineChart';
import AtlasV2TotalTrafficDoubleDropdownTopEntity from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficDoubleDropdownTopEntity';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2TrafficDropdownTopEntityTraffic from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficDropdownTopEntityTraffic';
import AtlasV2FilterableStackedBarChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalFilterableStackedBarChart';
import {
  useBrandEntity,
  useBusinessUnitEntity,
  useCategoryEntity,
  useProductEntity,
  useSegmentEntity,
  useSubcategoryEntity
} from 'src/utils/Hooks/reduxSelectors';
import { AtlasV2TrafficProductGrid } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2ProductGrid';
import AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2BrandedUnbrandedSplineChart';
import { shouldShowBrandUnbranded } from 'src/utils/app';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';
import { ChartContextProvider, ChartContextGroupProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { useDorisSummary } from 'src/serverProxy/useDorisSummary';
import saveAs from 'file-saver';
import { useAppSelector } from 'src/utils/Hooks';

/**
 * Atlas V2 Total Traffic V2 Page layout - Tab name: traffic subtab name: totalclicks
 */
export const AtlasV2TotalTrafficV2PageLayout = () => {
  const trafficSectionRef = useRef<HTMLDivElement>(null);

  const { primaryData: trafficPrimaryData, comparisonData: trafficComparisonData, isLoading: isTrafficLoading } = useDorisSummary({
    requestName: 'total-traffic-top-section',
    metricType: 'ClicksSummary',
    retailerId: 1
  });

  const mainTimePeriodDisplayName = useAppSelector((state) => state.mainTimePeriod.displayName);
  const comparisonTimePeriodDisplayName = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const convertTrafficDonutToCsv = useCallback(async () => {
    const rows = [
      ['Traffic Type', comparisonTimePeriodDisplayName, mainTimePeriodDisplayName],
      ['Organic Traffic', trafficComparisonData?.organicClicks ?? 0, trafficPrimaryData?.organicClicks ?? 0],
      ['Paid Search Traffic', trafficComparisonData?.adClicks ?? 0, trafficPrimaryData?.adClicks ?? 0],
      ['Other Traffic', trafficComparisonData?.otherClicks ?? 0, trafficPrimaryData?.otherClicks ?? 0],
      ['Total Traffic', trafficComparisonData?.totalClicks ?? 0, trafficPrimaryData?.totalClicks ?? 0]
    ];
    const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'Traffic.csv');
  }, [trafficPrimaryData, trafficComparisonData, mainTimePeriodDisplayName, comparisonTimePeriodDisplayName]);
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();
  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isSegmentEntity } = useSegmentEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const { isProductEntity } = useProductEntity();

  if (isRedirecting) return null;
  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        {/* ── Traffic (donut + bar charts) ── */}
        <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.TRAFFIC.name}>
          <div
            ref={trafficSectionRef}
            style={{
              position: 'relative'
            }}
          >
            <BeaconChartWithLegend
              title="Traffic"
              showExportMenu={true}
              saveImageRef={trafficSectionRef}
              convertSeriesToDelimitedData={convertTrafficDonutToCsv}
              loading={isTrafficLoading}
            >
              <AtlasV2TrafficTopSectionV2 externalContainerRef={trafficSectionRef} />
            </BeaconChartWithLegend>
          </div>
        </ChartContextGroupProvider>

        {/* ── Total Traffic spline chart ── */}
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.TOTAL_TRAFFIC.name}
          chartName={CHART_NAMES.TOTAL_TRAFFIC_SPLINE}
          hideSubtitle
        >
          <div style={{ marginTop: '-49px' }}>
            <AtlasV2TotalTrafficV2TotalTrafficSplineChart />
          </div>
        </ChartContextProvider>

        {/* ── Share of Total Traffic spline chart ── */}
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.SHARE_OF_TRAFFIC.name}
          chartName={CHART_NAMES.SHARE_OF_TRAFFIC_SPLINE}
          hideSubtitle
        >
          <AtlasV2TotalTrafficV2ShareOfTrafficSplineChart />
        </ChartContextProvider>

        {/* ── Traffic by Keyword column chart (shared component, hardcodes its own chartName) ── */}
        {!isCategoryEntity && !isSubcategoryEntity && !isSegmentEntity && !isBusinessUnitEntity && !isBrandEntity && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.TRAFFIC_BY_KEYWORD.name} hideSubtitle>
            <AtlasV2TrafficDropdownTopEntityTraffic
              groupByFields={[{ id: 'keyword', label: 'Keyword', indexName: 'traffic' }]}
              queryKey="totalClicks_singleDropdown"
              fields={[
                {
                  fieldName: 'totalClicks',
                  label: 'Total Traffic',
                  metricType: 'totalClicks',
                  metricTypeDisplay: METRICTYPE.VOLUME
                },
                {
                  fieldName: 'totalClicksShare',
                  label: 'Share of Total Traffic',
                  metricType: 'totalClicks',
                  metricTypeDisplay: METRICTYPE.PERCENT,
                  isMarketShare: true
                }
              ]}
            />
          </ChartContextProvider>
        )}

        {/* ── Branded/Unbranded spline chart (shared component, hardcodes its own chartName) ── */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) &&
          shouldShowBrandUnbranded() && (
            <ChartContextProvider group={CHART_CONTEXT_GROUPS.BRANDED_UNBRANDED_TRAFFIC.name} hideSubtitle>
              <AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart fieldName="totalClicks" />
            </ChartContextProvider>
          )}

        {/* ── Top Entity Traffic column chart ── */}
        {!isProductEntity && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.TRAFFIC_TOP_ENTITY.name}
            chartName={CHART_NAMES.TRAFFIC_TOP_ENTITY_COLUMN}
            hideSubtitle
          >
            <AtlasV2TotalTrafficDoubleDropdownTopEntity />
          </ChartContextProvider>
        )}

        {/* ── Filterable Stacked Bar (keyword) -- shared component, hardcodes its own chartName ── */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.FILTERABLE_STACKED_BAR.name}>
            <AtlasV2FilterableStackedBarChart
              metricDropdownOptions={[
                {
                  id: 'totalTraffic',
                  label: 'Total Traffic',
                  apiMetricFieldName: 'totalClicks',
                  subtitleLabel: 'total traffic'
                },
                {
                  id: 'totalTrafficPercent',
                  label: '% of Total Traffic',
                  apiMetricFieldName: 'totalClicks',
                  isPercentageVisible: true,
                  defaultMetric: 'totalTraffic',
                  subtitleLabel: 'percentage of total traffic'
                }
              ]}
              groupByFieldOptions={[
                {
                  id: 'keyword',
                  label: 'Keyword'
                }
              ]}
            />
          </ChartContextProvider>
        )}

        {/* ── Keyword Count with Total Traffic spline chart ── */}
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.KEYWORD_COUNT_TRAFFIC.name}
          chartName={CHART_NAMES.KEYWORD_COUNT_TRAFFIC_SPLINE}
          hideSubtitle
        >
          <AtlasV2TotalTrafficKeywordCountWithTotalTraffic />
        </ChartContextProvider>

        {/* ── Brand → Category/Subcategory top entity chart ── */}
        {isBrandEntity && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.TRAFFIC_TOP_ENTITY_BRAND.name}
            chartName={CHART_NAMES.TRAFFIC_TOP_ENTITY_COLUMN}
            hideSubtitle
          >
            <AtlasV2TotalTrafficDoubleDropdownTopEntity
              groupByFields={[
                { id: 'category', label: 'Category', indexName: 'traffic' },
                { id: 'subcategory', label: 'Subcategory', indexName: 'traffic' }
              ]}
            />
          </ChartContextProvider>
        )}

        {/* ── Filterable Stacked Bar (category/subcategory) -- shared component, hardcodes its own chartName ── */}
        {(isCategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.FILTERABLE_STACKED_BAR_CATEGORY.name}>
            <AtlasV2FilterableStackedBarChart
              metricDropdownOptions={[
                {
                  id: 'totalTraffic',
                  label: 'Total Traffic',
                  apiMetricFieldName: 'totalClicks',
                  subtitleLabel: 'total traffic'
                },
                {
                  id: 'totalTrafficPercent',
                  label: '% of Total Traffic',
                  apiMetricFieldName: 'totalClicks',
                  isPercentageVisible: true,
                  defaultMetric: 'totalTraffic',
                  subtitleLabel: 'percentage of total traffic'
                }
              ]}
              groupByFieldOptions={[
                ...(isAllCategoriesEntity || isSegmentEntity || isBusinessUnitEntity
                  ? [
                    {
                      id: 'category',
                      label: 'Category'
                    }
                  ]
                  : []),
                {
                  id: 'subcategory',
                  label: 'Subcategory'
                }
              ]}
            />
          </ChartContextProvider>
        )}

        <ChartContextProvider disableSubtitleFallback hideSubtitle>
          <AtlasV2TrafficProductGrid trafficType="totalClicks" />
        </ChartContextProvider>
      </Flex>
    </BasePageContainer>
  );
};
